import React, { useEffect, useState } from 'react';
import { Empty, Space, Table, Tag, Typography } from 'antd';
import DashboardMetric from './DashboardMetric';
import lib from '../../../assets/utils/lib';

const { Text } = Typography;
const PALETTE = ['#1677ff', '#52c41a', '#722ed1', '#fa8c16', '#eb2f96', '#13c2c2', '#f5222d', '#2f54eb', '#a0d911', '#faad14'];

/**
 * Data-driven encounter analytics for the main dashboard (testing finding #1):
 * a tile per check-in type (every type, legacy included, new ones appear on
 * their own), how check-ins were created (manual / direct) as a separate
 * breakdown, and encounters per Benefit Item from item mapping — the one
 * source of truth for "how many surgeries". Reads /v2/dashboard/encounters,
 * open to any signed-in staff member. Renders nothing when unreachable.
 */
export default function EncountersBreakdown({ from, to, onTypeClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    lib.dashboardEncounters({ from, to }).then((d) => { if (alive) { setData(d); setLoading(false); } });
    return () => { alive = false; };
  }, [from, to]);

  if (!data && !loading) return null;
  const types = data?.by_checkin_type || [];
  const channels = data?.by_channel || [];
  const benefits = data?.by_benefit_item_encounters || [];
  const rangeLabel = data ? `${data.from} → ${data.to}` : '';

  // Rendered inside DashboardSection's `.dash-metric-grid`, so this block must
  // span every column; otherwise it becomes one more cell beside the summary
  // tiles, which then stretch to its height with their numbers out of view.
  return (
    <div style={{ marginTop: 4, gridColumn: '1 / -1', minWidth: 0 }}>
      <Space align="baseline" style={{ marginBottom: 6 }} wrap>
        <Text strong>By check-in type</Text>
        <Text type="secondary" style={{ fontSize: 12 }}>{rangeLabel} · every type the data holds, including legacy ones</Text>
      </Space>
      <div className="dash-metric-grid">
        {types.map((t, i) => (
          <DashboardMetric
            key={t.checkin_type}
            title={t.checkin_type}
            value={t.encounters}
            color={PALETTE[i % PALETTE.length]}
            variant="tile"
            loading={loading}
            onClick={onTypeClick ? () => onTypeClick(t.checkin_type) : undefined}
            subtitle="encounters"
          />
        ))}
      </div>

      {channels.length ? (
        <div style={{ marginTop: 12 }}>
          <Text strong>How check-ins were created</Text>
          <Text type="secondary" style={{ fontSize: 12 }}> — not a check-in type, so shown separately</Text>
          <div style={{ marginTop: 4 }}>
            <Space wrap>
              {channels.map((c) => (
                <Tag key={c.channel} style={{ fontSize: 13, padding: '4px 10px' }}>
                  {c.channel}: <b>{Number(c.encounters).toLocaleString()}</b>
                </Tag>
              ))}
            </Space>
          </div>
        </div>
      ) : null}

      <div style={{ marginTop: 12 }}>
        <Text strong>By benefit item</Text>
        <Text type="secondary" style={{ fontSize: 12 }}> — from item mapping only (e.g. Surgical Care, Specialist Consultations)</Text>
        {benefits.length ? (
          <Table
            size="small"
            style={{ marginTop: 4, maxWidth: 640 }}
            rowKey="benefit_item"
            pagination={benefits.length > 10 ? { pageSize: 10, size: 'small' } : false}
            dataSource={benefits}
            columns={[
              { title: 'Benefit item', dataIndex: 'benefit_item', key: 'b' },
              { title: 'Encounters', dataIndex: 'encounters', key: 'e', width: 120, align: 'right', render: (v) => Number(v).toLocaleString() },
              { title: 'Lines', dataIndex: 'lines', key: 'l', width: 100, align: 'right', render: (v) => Number(v).toLocaleString() },
            ]}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No mapped items in this period yet — map items from PA, Claims or Encounters and they appear here." style={{ margin: '8px 0' }} />
        )}
      </div>
    </div>
  );
}
